import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, LogOut, Users, CheckCircle2, Clock, Phone, Mail, MapPin, Search, Package,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ProductsPanel from "../components/ProductsPanel";

const STATUS_COLORS = {
  new: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/40",
  contacted: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/40",
  converted: "bg-[#D1FAE5] text-[#065F46] border-[#10B981]/40",
};

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("leads");

  const loadData = useCallback(async () => {
    const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
    setLeads(l.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    if (location.state?.user) {
      loadData();
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); return loadData(); })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setChecking(false));
  }, [loadData, navigate, location.state]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      loadData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return l.name.toLowerCase().includes(s) || l.email.toLowerCase().includes(s) ||
      l.phone.includes(s) || l.city.toLowerCase().includes(s);
  });

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-[#064E3B] border-t-transparent animate-spin"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 bg-[#064E3B] text-white flex-col p-6 min-h-screen sticky top-0">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-[#F59E0B] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#064E3B]"/>
          </div>
          <span className="font-display font-black">5GB Admin</span>
        </Link>

        <nav className="mt-10 flex-1 space-y-1">
          <button
            onClick={() => setTab("leads")}
            data-testid="nav-tab-leads"
            className={`w-full text-left px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors ${tab === "leads" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
          >
            <Users className="h-4 w-4"/> Leads
          </button>
          <button
            onClick={() => setTab("products")}
            data-testid="nav-tab-products"
            className={`w-full text-left px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors ${tab === "products" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
          >
            <Package className="h-4 w-4"/> Products
          </button>
        </nav>

        {user && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-9 w-9 rounded-full"/>
              ) : (
                <div className="h-9 w-9 rounded-full bg-white/10"/>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user.name}</div>
                <div className="text-xs text-white/60 truncate">{user.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="mt-4 w-full text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-[#F59E0B] flex items-center gap-2"
            >
              <LogOut className="h-4 w-4"/> Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-[#E7E5E4] bg-white">
          <div className="px-6 md:px-10 py-6 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C]">Dashboard</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black text-[#064E3B]">
                {tab === "leads" ? "Lead management" : "Product catalog"}
              </h1>
            </div>
            {/* Mobile tab switcher */}
            <div className="md:hidden">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="leads" data-testid="mobile-tab-leads">Leads</TabsTrigger>
                  <TabsTrigger value="products" data-testid="mobile-tab-products">Products</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {tab === "leads" && (
            <div className="px-6 md:px-10 pb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total leads" value={stats.total} icon={Users} color="#064E3B" testid="stat-total"/>
              <StatCard label="New" value={stats.new} icon={Clock} color="#F59E0B" testid="stat-new"/>
              <StatCard label="Contacted" value={stats.contacted} icon={Phone} color="#2563EB" testid="stat-contacted"/>
              <StatCard label="Converted" value={stats.converted} icon={CheckCircle2} color="#10B981" testid="stat-converted"/>
            </div>
          )}
        </div>

        <div className="px-6 md:px-10 py-6">
          {tab === "products" ? (
            <ProductsPanel />
          ) : (
          <>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]"/>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search"
                placeholder="Search name, email, phone, city..."
                className="w-full h-11 pl-10 pr-4 rounded-md border border-[#E7E5E4] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C2410C]"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]" data-testid="admin-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border border-[#E7E5E4] bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F4]">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-16 text-[#78716C]">
                      No leads to show yet.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <div className="font-semibold text-[#064E3B]">{l.name}</div>
                      {l.interest && (
                        <div className="text-xs text-[#78716C] mt-1 max-w-xs truncate">{l.interest}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm"><Phone className="h-3.5 w-3.5 text-[#78716C]"/>{l.phone}</div>
                      <div className="flex items-center gap-1.5 text-xs text-[#78716C] mt-1"><Mail className="h-3 w-3"/>{l.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm"><MapPin className="h-3.5 w-3.5 text-[#78716C]"/>{l.city}</div>
                      <div className="text-xs text-[#78716C] mt-1">{l.state} · {l.pincode}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[l.status] || ""} border font-bold uppercase tracking-wider text-[10px]`}>
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={l.status}
                        onValueChange={(v) => updateStatus(l.id, v)}
                      >
                        <SelectTrigger className="w-[140px] ml-auto" data-testid={`lead-status-${l.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
          )}
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, color, testid }) => (
  <div className="bg-white border border-[#E7E5E4] rounded-md p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#57534E]">{label}</div>
      <Icon className="h-4 w-4" style={{ color }}/>
    </div>
    <div className="font-display text-3xl font-black text-[#064E3B] mt-3">{value}</div>
  </div>
);
